import { cloneDeep } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TASK_CONST, TOOL_CONST } from '@/constant';
import {
  AddBox,
  CheckCircle,
  CheckCircleLine,
  Close,
  Done,
  VFlex,
  VStack,
  VTextButton,
} from '@/package';
import {
  useLabelingBoxStore,
  useReviewStore,
  useTaskFrameStore,
  useToolPermissionStore,
} from '@/store';
import type { BoxWithLabel } from '@/type';

export const ToolRejectReasonBoxViewer = () => {
  const { t } = useTranslation();
  const [currentFrame, isFrameDataLoading, isWorkState] = useTaskFrameStore(
    state => [state.currentFrame, state.isFrameDataLoading, state.isWorkState],
  );
  const permission = useToolPermissionStore(state => state.permission);
  const getCurrentPcdIndexReviewBoxList = useReviewStore(
    state => state.getCurrentPcdIndexReviewBoxList,
  );
  const getCurrentBoxList = useMemo(
    () => useLabelingBoxStore.getState().getCurrentBoxList,
    [],
  );
  const setBoxList = useMemo(
    () => useLabelingBoxStore.getState().setBoxList,
    [],
  );
  const addReviewBox = useMemo(() => useReviewStore.getState().addBox, []);
  const removeReviewBox = useMemo(
    () => useReviewStore.getState().removeBox,
    [],
  );

  const rejectReasonBoxList =
    getCurrentPcdIndexReviewBoxList()?.map(box => {
      return {
        ...box,
        index: getCurrentBoxList().findIndex(b => b.id === box.id) + 1,
      };
    }) ?? [];

  const isRejectedReasonBoxListExist = rejectReasonBoxList.length;

  const onConvertToWorkDataClick = (reviewBox: BoxWithLabel) => {
    const toConvertWorkingBox = cloneDeep(reviewBox);
    toConvertWorkingBox.workType = TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE.WORK;

    setBoxList([...getCurrentBoxList(), toConvertWorkingBox]);
    removeReviewBox(reviewBox.id);
  };

  const onConvertToReviewDataClick = (workBox: BoxWithLabel) => {
    const newBoxList = getCurrentBoxList().filter(box => box.id !== workBox.id);

    setBoxList(newBoxList);
    addReviewBox(workBox);
  };

  const isConvertedWorkBox = (box: BoxWithLabel) => {
    return (
      getCurrentPcdIndexReviewBoxList().find(b => b.id === box.id) &&
      getCurrentBoxList().find(b => b.id === box.id)
    );
  };

  const onConvertAllToReviewClick = () => {
    getCurrentPcdIndexReviewBoxList()
      .filter(b => !isConvertedWorkBox(b))
      .forEach(b => onConvertToWorkDataClick(b));
  };

  return (
    <VStack className="w-full flex-1 gap-[2px] overflow-hidden pt-2">
      <VFlex items="center" className="w-full" justify="between">
        <div className="text-body-md font-semibold">
          {t('review.feedbackBoxes.expressiveness')}
        </div>

        {permission.labeling.updatable &&
          isWorkState(TASK_CONST.TASK_FRAME_WORKSTATE.WORKING) && (
            <VTextButton onClick={onConvertAllToReviewClick}>
              <VFlex gap={0.25} items="center" className="text-body-md">
                <Done className="size-[14px]" />
                <span>{t('review.convertAllToObject.aghast')}</span>
              </VFlex>
            </VTextButton>
          )}
      </VFlex>

      <VStack className="w-full flex-1 gap-[2px] overflow-y-auto">
        {!isRejectedReasonBoxListExist && (
          <VStack
            gap={0.25}
            className="w-full py-10 text-gray-300"
            items="center"
          >
            <AddBox className="size-8" />
            <div className="text-body-md">
              {t('review.noFeedbackItems.nivation')}
            </div>
          </VStack>
        )}
        {rejectReasonBoxList.map((box, index) => {
          const { Icon, onClick, iconClassName } = (() => {
            const isConvertedToWork = !!isConvertedWorkBox(box);

            if (
              currentFrame?.workState === 'Reviewing' &&
              permission.labelingReview.updatable
            )
              return {
                Icon: Close,
                onClick: isConvertedToWork
                  ? () => {}
                  : () => useReviewStore.getState().removeBox(box.id),
                iconClassName: isConvertedToWork
                  ? 'hidden'
                  : 'size-4 text-gray-400',
              };

            if (
              currentFrame?.workState === 'Working' &&
              permission.labeling.updatable &&
              isConvertedToWork
            )
              return {
                Icon: CheckCircle,
                onClick: () => onConvertToReviewDataClick(box),
                iconClassName: 'size-4 text-success-500',
              };

            if (
              currentFrame?.workState === 'Working' &&
              permission.labeling.updatable &&
              !isConvertedToWork
            )
              return {
                Icon: CheckCircleLine,
                onClick: () => onConvertToWorkDataClick(box),
                iconClassName: 'size-4 text-primary-500',
              };

            return { Icon: null, onClick: null, iconClassName: '' };
          })();

          return (
            <VFlex gap={0.25} className="w-full" items="center" key={box.id}>
              <VFlex
                className="flex-1 rounded-[2px] border border-gray-200 px-2 py-1"
                gap={0.5}
                role="button"
                onClick={() => useLabelingBoxStore.getState().selectBox(box.id)}
              >
                <span className="text-body-md text-gray-500">#{index + 1}</span>
                <span className="text-body-md">
                  {t(
                    TOOL_CONST.LABELING_TRANSLATION_KEY_MAPPER[
                      box.label as keyof typeof TOOL_CONST.LABELING_TRANSLATION_KEY_MAPPER
                    ],
                  )}
                </span>
              </VFlex>

              {Icon && (
                <div
                  className={'p-[5px]'}
                  role="button"
                  onClick={() => !isFrameDataLoading && onClick?.()}
                >
                  <Icon className={iconClassName} />
                </div>
              )}
            </VFlex>
          );
        })}
      </VStack>
    </VStack>
  );
};

ToolRejectReasonBoxViewer.displayName = 'ToolRejectReasonBoxViewer';
